import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { AssignedSpecialistCard } from 'features/consultations/AssignedSpecialistCard';
import { canRequestConsultation, isActiveConsultationStatus, publicConsultationView } from 'features/consultations/consultationUtils';
import { userApi } from 'features/home/home.api';
import { HomeHeroIllustration } from 'shared/assets/illustrations';
import {
  IconAssessment,
  IconConsultations,
  IconNotifications,
  IconSupport,
} from 'shared/assets/icons';
import { Card, ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { PatternChip } from 'shared/components/PatternBar';
import { formatDateTime, formatDateTimeInTimeZone } from 'shared/utilities/format';

export function HomePage() {
  const { t, locale } = useI18n();
  const homeQuery = useQuery({
    queryKey: queryKeys.home,
    queryFn: () => userApi.home(),
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });

  if (homeQuery.isLoading) {
    return <Skeleton rows={8} variant="cards" />;
  }
  if (homeQuery.isError) {
    return <ErrorState title={humanizeApiError(homeQuery.error, t)} onRetry={() => homeQuery.refetch()} retryLabel={t('actions.retry')} />;
  }

  const data = homeQuery.data || {};
  const name = data.user?.name || '';
  const attempt = data.in_progress_attempt;
  const result = data.latest_result;
  const consultation = publicConsultationView(data.consultation);
  const hasConsultation = Boolean(consultation.id);
  const activeConsultation = isActiveConsultationStatus(consultation.status);
  const allowRequest = canRequestConsultation(data);
  const unread = Number(data.unread_notification_count || 0);
  const scheduled = formatDateTimeInTimeZone(consultation.scheduled_at, locale, consultation.display_timezone);

  return (
    <div className="home-page">
      <PageMeta titleKey="meta.home" />
      <section className="home-hero card">
        <div className="home-hero-copy">
          <p className="kicker">{t('appTagline')}</p>
          <h1>{name ? t('home.greeting', { name }) : t('home.greetingGuest')}</h1>
          <p>
            {attempt
              ? t('home.inProgress', {
                answered: attempt.saved_answer_count ?? 0,
                total: attempt.total_question_count ?? 0,
              })
              : (data.assessment ? t('home.assessmentReady') : t('home.assessmentUnavailable'))}
          </p>
          {attempt ? (
            <Link className="btn btn-primary btn-lg" to={paths.assessmentAttempt(attempt.id)}>{t('actions.resumeAssessment')}</Link>
          ) : data.assessment ? (
            <Link className="btn btn-primary btn-lg" to={paths.assessmentIntro}>{t('actions.startAssessment')}</Link>
          ) : null}
        </div>
        <div className="home-hero-visual" aria-hidden="true">
          <HomeHeroIllustration />
        </div>
      </section>
      <div className="home-status-grid">
        <Card>
          <div className="icon-tile" aria-hidden="true"><IconAssessment /></div>
          <h2>{t('home.latestResult')}</h2>
          {result ? (
            <>
              <p className="profile-code">{result.profile_code}</p>
              <p className="muted">{formatDateTime(result.completed_at, locale)}</p>
              <div className="chip-row">
                {(result.top_patterns || []).map((pattern) => (
                  <PatternChip key={pattern.id || pattern.short_code} pattern={pattern} locale={locale} />
                ))}
              </div>
            </>
          ) : (
            <p className="muted">{t('home.noResult')}</p>
          )}
        </Card>
        {hasConsultation ? (
          <Link
            className="card card-interactive home-consultation-card"
            to={paths.consultation(consultation.id)}
            data-testid="home-consultation-card"
          >
            <div className="icon-tile" aria-hidden="true"><IconConsultations /></div>
            <h2>{t('home.consultation')}</h2>
            <p>{consultation.reference}</p>
            <StatusChip tone={consultation.status}>{consultation.status_label || t(`consultations.statuses.${consultation.status}`)}</StatusChip>
            {consultation.scheduled_at ? (
              <p className="muted">
                {scheduled.datetime}
                {scheduled.timeZoneLabel ? ` ${scheduled.timeZoneLabel}` : ''}
              </p>
            ) : null}
            <AssignedSpecialistCard consultation={consultation} compact />
          </Link>
        ) : (
          <Card>
            <div className="icon-tile" aria-hidden="true"><IconConsultations /></div>
            <h2>{t('home.consultation')}</h2>
            <p className="muted">{t('home.noConsultation')}</p>
          </Card>
        )}
        <Card>
          <div className="icon-tile" aria-hidden="true"><IconNotifications /></div>
          <h2>{t('nav.notifications')}</h2>
          <p>{t('home.unread', { count: unread })}</p>
        </Card>
      </div>
      <div className="home-actions">
        {result ? (
          <Link className="card card-interactive" to={paths.result(result.attempt_id)}>
            <div className="icon-tile"><IconAssessment /></div>
            <strong>{t('actions.viewResult')}</strong>
          </Link>
        ) : null}
        <Link className="card card-interactive" to={paths.history}>
          <div className="icon-tile"><IconAssessment /></div>
          <strong>{t('actions.viewHistory')}</strong>
        </Link>
        {activeConsultation || hasConsultation ? (
          <Link className="card card-interactive" to={paths.consultation(consultation.id)} data-testid="home-view-consultation">
            <div className="icon-tile"><IconConsultations /></div>
            <strong>{t('actions.viewConsultation')}</strong>
          </Link>
        ) : null}
        {allowRequest ? (
          <Link className="card card-interactive" to={paths.consultationNew} data-testid="home-request-consultation">
            <div className="icon-tile"><IconConsultations /></div>
            <strong>{t('actions.requestConsultation')}</strong>
          </Link>
        ) : null}
        <Link className="card card-interactive" to={paths.notifications}>
          <div className="icon-tile"><IconNotifications /></div>
          <strong>{t('nav.notifications')}</strong>
        </Link>
        <Link className="card card-interactive" to={paths.support}>
          <div className="icon-tile"><IconSupport /></div>
          <strong>{t('nav.support')}</strong>
        </Link>
      </div>
    </div>
  );
}
